import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/ToastContainer";
import { moduleService } from "../services/moduleService";
import { lessonService } from "../services/lessonService";
import { uploadAttachment } from "../services/uploadService";
import { parseApiError, formatValidationErrors } from "../utils/errorHandler";
import {
  ArrowLeft,
  Loader2,
  Save,
  Upload,
  Trash2,
  Video,
  FileText,
  Calculator,
  Clock,
} from "lucide-react";
import { videoService } from "../services/videoService";

interface Attachment {
  name: string;
  url: string;
  fileType: string;
  size: number;
}

interface FormData {
  title: string;
  description: string;
  order: number;
  duration: number;
  isFree: boolean;
  isPublished: boolean;
  contentType: "video" | "article";
  videoUrl: string;
  vdocipherVideoId: string;
  articleText: string;
  attachments: Attachment[];
  isManualDuration: boolean;
}

export default function AddEditLessonPage() {
  const navigate = useNavigate();
  const {
    id: courseId,
    moduleId,
    lessonId,
  } = useParams<{
    id: string;
    moduleId: string;
    lessonId?: string;
  }>();
  const isEditMode = Boolean(lessonId);

  const [moduleName, setModuleName] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [attachmentUploadProgress, setAttachmentUploadProgress] = useState<
    Record<string, number>
  >({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});
  const { showSuccess, showError } = useToast();

  const [formData, setFormData] = useState<FormData>({
    title: "",
    description: "",
    order: 1,
    duration: 0,
    isFree: false,
    isPublished: false,
    contentType: "video",
    videoUrl: "",
    vdocipherVideoId: "",
    articleText: "",
    attachments: [],
    isManualDuration: false,
  });

  const inputClassName =
    "w-full px-4 py-3 bg-white border border-[#DCE0E5] rounded-[8px] text-[14px] text-[#102040] placeholder:text-[#706F6F] focus:outline-none focus:ring-2 focus:ring-[#e07828]/20 focus:border-[#e07828] transition-all";

  const formatDuration = (seconds: number) => {
    if (!seconds || seconds <= 0) return "0s";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    const parts = [];
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);
    if (s > 0 || parts.length === 0) parts.push(`${s}s`);

    return parts.join(" ");
  };

  /**
   * Read duration from a local video File before/without waiting for VdoCipher.
   * Length from the provider is often 0 while the video is still processing.
   */
  const getLocalVideoDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        const d = Math.round(video.duration);
        URL.revokeObjectURL(url);
        if (!Number.isFinite(d) || d <= 0) {
          reject(new Error("Could not read video duration"));
          return;
        }
        resolve(d);
      };
      video.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Could not read video metadata"));
      };
      video.src = url;
    });
  };

  // Fetch module name
  useEffect(() => {
    const fetchModule = async () => {
      if (!moduleId) return;
      try {
        const response = await moduleService.getModuleById(moduleId);
        setModuleName(response.module?.title || "");
      } catch (err) {
        console.error("Failed to fetch module:", err);
      }
    };
    fetchModule();
  }, [moduleId]);

  // Fetch lesson data if editing
  const fetchLesson = useCallback(async () => {
    if (!lessonId) return;
    setIsFetching(true);
    try {
      const response = await lessonService.getLessonById(lessonId);
      const lesson = response.lesson;

      // Determine content type from lesson data
      const content = lesson.content as any;
      let contentType: "video" | "article" = "video";
      if (content?.articleText) {
        contentType = "article";
      }

      // Check if duration was manually set vs auto-calculated
      // Auto-calculated durations are typically exact values from video APIs
      // Manual durations are often round numbers or specific values that don't match video length
      let isManualDuration = false;
      if (contentType === "video" && content?.vdocipherVideoId) {
        // Check if stored duration might be auto-calculated
        // If the video ID exists and has data, we can recalculate
        isManualDuration = false;
      } else {
        // For YouTube URLs or articles, default to manual to avoid unexpected changes
        isManualDuration = true;
      }

      setFormData({
        title: lesson.title || "",
        description: (lesson as any).description || "",
        order: lesson.order || 1,
        duration: lesson.duration || 0,
        isFree: lesson.isFree || false,
        isPublished: lesson.isPublished || false,
        contentType,
        videoUrl: content?.videoUrl || "",
        vdocipherVideoId: content?.vdocipherVideoId || "",
        articleText: content?.articleText || "",
        attachments: content?.attachments || [],
        isManualDuration,
      });
    } catch (err: any) {
      console.error("Failed to fetch lesson:", err);
      const apiError = parseApiError(err);
      setError(apiError.message);
      showError(apiError.message, "Failed to Load Lesson");
    } finally {
      setIsFetching(false);
    }
  }, [lessonId]);

  useEffect(() => {
    if (isEditMode) {
      fetchLesson();
    }
  }, [isEditMode, fetchLesson]);

  // Auto-set order for new lessons
  useEffect(() => {
    const fetchLessonsForOrder = async () => {
      if (isEditMode || !moduleId) return;
      try {
        const response = await moduleService.getModuleById(moduleId);
        const lessons = response.module?.lessons || [];
        const maxOrder = lessons.reduce(
          (max: number, l: any) => Math.max(max, l.order || 0),
          0,
        );
        setFormData((prev) => ({ ...prev, order: maxOrder + 1 }));
      } catch (err) {
        console.error("Failed to fetch lessons for order:", err);
      }
    };
    fetchLessonsForOrder();
  }, [moduleId, isEditMode]);

  // Automatic Duration Calculation
  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;

    const calculateDuration = async () => {
      if (formData.isManualDuration) return;

      // 1. Article Content
      if (formData.contentType === "article" && formData.articleText) {
        // Estimate: 200 words per minute
        const wordCount = formData.articleText.trim().split(/\s+/).length;
        const seconds = Math.ceil((wordCount / 200.0) * 60);
        if (!cancelled) {
          setFormData((prev) => ({ ...prev, duration: seconds }));
        }
        return;
      }

      // 2. Video Content — only apply positive durations (never overwrite with 0)
      if (formData.contentType === "video") {
        let provider: "youtube" | "vdocipher" | null = null;
        let idOrUrl = "";

        if (formData.vdocipherVideoId && formData.vdocipherVideoId.length > 5) {
          provider = "vdocipher";
          idOrUrl = formData.vdocipherVideoId;
        } else if (
          formData.videoUrl &&
          (formData.videoUrl.includes("youtube.com") ||
            formData.videoUrl.includes("youtu.be"))
        ) {
          provider = "youtube";
          idOrUrl = formData.videoUrl;
        }

        if (provider && idOrUrl) {
          setIsCalculating(true);
          try {
            const maxAttempts = provider === "vdocipher" ? 6 : 1;
            for (let attempt = 0; attempt < maxAttempts; attempt++) {
              if (cancelled) return;
              const meta = await videoService.getVideoMetadata(
                provider,
                idOrUrl,
              );
              const nextDuration =
                meta && typeof meta.duration === "number" ? meta.duration : 0;
              const status = (meta as { videoStatus?: string })?.videoStatus;

              if (nextDuration > 0) {
                if (!cancelled) {
                  setFormData((prev) => ({
                    ...prev,
                    // Prefer a newly confirmed API duration over stale values
                    duration: nextDuration,
                  }));
                }
                break;
              }

              // Still processing on VdoCipher — wait and retry without clearing duration
              if (
                provider === "vdocipher" &&
                (status === "processing" || !status) &&
                attempt < maxAttempts - 1
              ) {
                await new Promise<void>((resolve) => {
                  pollTimer = setTimeout(resolve, 2500);
                });
                continue;
              }
              break;
            }
          } catch (err) {
            console.error("Failed to calculate duration", err);
          } finally {
            if (!cancelled) setIsCalculating(false);
          }
        }
      }
    };

    const timeoutId = setTimeout(calculateDuration, 1000); // 1s debounce
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [
    formData.contentType,
    formData.articleText,
    formData.vdocipherVideoId,
    formData.videoUrl,
    formData.isManualDuration,
  ]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
            ? Number(value)
            : value,
    }));
  };

  const handleManualDurationChange = (minutes: number, seconds: number) => {
    const totalSeconds = Math.max(0, minutes * 60 + (seconds || 0));
    setFormData((prev) => ({
      ...prev,
      duration: totalSeconds,
      isManualDuration: true,
    }));
  };

  const processFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    // Initialize all files with 0% progress
    const initialProgress: Record<string, number> = {};
    Array.from(files).forEach((file) => {
      initialProgress[file.name] = 0;
    });
    setAttachmentUploadProgress(initialProgress);

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const url = await uploadAttachment(file, (progress: number) => {
          setAttachmentUploadProgress((prev) => ({
            ...prev,
            [file.name]: progress,
          }));
        });

        // Use the actual file extension
        const extension = file.name.split(".").pop()?.toLowerCase() || "other";
        const fileType = extension;

        return {
          name: file.name,
          url,
          fileType,
          size: file.size,
        };
      });

      const newAttachments = await Promise.all(uploadPromises);
      setFormData((prev) => ({
        ...prev,
        attachments: [...prev.attachments, ...newAttachments],
      }));
    } catch (err) {
      console.error("Failed to upload attachment:", err);
      const apiError = parseApiError(err);
      setError(apiError.message);
      showError(apiError.message, "Upload Failed");
    } finally {
      setIsUploading(false);
      setAttachmentUploadProgress({});
    }
  };

  const handleAttachmentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    processFiles(e.target.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    processFiles(e.dataTransfer.files);
  };

  const removeAttachment = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file) return;

    setIsUploadingVideo(true);
    setUploadProgress(0);
    setError(null);

    // Read duration from the local file first — VdoCipher length is often 0
    // until encoding finishes, which previously saved lessons as 0s.
    let localDuration = 0;
    try {
      localDuration = await getLocalVideoDuration(file);
      if (localDuration > 0) {
        setFormData((prev) => ({
          ...prev,
          duration: localDuration,
          isManualDuration: false,
        }));
      }
    } catch (metaErr) {
      console.warn("Local video duration unavailable:", metaErr);
    }

    try {
      // 1. Get Credentials
      const creds = await videoService.getUploadCredentials(
        formData.title || file.name,
      );

      // 2. Upload
      const videoId = await videoService.uploadToVdoCipher(
        file,
        creds,
        (progress: number) => {
          setUploadProgress(progress);
        },
      );

      // 3. Set ID; keep local duration if already set. API poll may refine later.
      setFormData((prev) => ({
        ...prev,
        vdocipherVideoId: videoId,
        isManualDuration: false,
        duration:
          prev.duration > 0
            ? prev.duration
            : localDuration > 0
              ? localDuration
              : prev.duration,
      }));
      showSuccess(
        "Video uploaded successfully to VdoCipher",
        "Upload Complete",
      );
    } catch (err: any) {
      console.error("Video upload failed:", err);
      setError(
        "Failed to upload video to VdoCipher: " +
          (err.message || "Unknown error"),
      );
    } finally {
      setIsUploadingVideo(false);
      // Reset file input
      e.target.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setValidationErrors({});

    try {
      const content: any = {};

      if (formData.contentType === "video") {
        if (formData.videoUrl) content.videoUrl = formData.videoUrl;
        if (formData.vdocipherVideoId)
          content.vdocipherVideoId = formData.vdocipherVideoId;
      } else {
        if (formData.articleText) content.articleText = formData.articleText;
      }

      if (formData.attachments.length > 0) {
        content.attachments = formData.attachments;
      }

      const payload = {
        title: formData.title,
        description: formData.description,
        order: formData.order,
        duration: formData.duration,
        isFree: formData.isFree,
        isPublished: formData.isPublished,
        module: moduleId!,
        content,
      };

      if (isEditMode && lessonId) {
        await lessonService.updateLesson(lessonId, payload);
        showSuccess("Lesson updated successfully", "Success");
      } else {
        const response = await lessonService.createLesson(payload);
        if (moduleId && response?.lesson?._id) {
          await moduleService.addLessonToModule(moduleId, response.lesson._id);
        }
        showSuccess("Lesson created successfully", "Success");
      }

      navigate(`/courses/${courseId}/structure`);
    } catch (err: any) {
      console.error("Failed to save lesson:", err);
      const apiError = parseApiError(err);

      if (apiError.errors && Object.keys(apiError.errors).length > 0) {
        setValidationErrors(apiError.errors);
        setError("Please fix the validation errors below.");
      } else {
        setError(apiError.message);
      }

      showError(
        apiError.message,
        isEditMode ? "Update Failed" : "Create Failed",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isFetching) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#e07828]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <form onSubmit={handleSubmit} className="w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Link
              to={`/courses/${courseId}/structure`}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-[#102040]">
                {isEditMode ? "Edit Lesson" : "Add New Lesson"}
              </h1>
              <p className="text-[#706F6F] text-sm">
                Module: {moduleName || "Loading..."}
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 font-medium">{error}</p>
            {Object.keys(validationErrors).length > 0 && (
              <ul className="mt-2 ml-4 list-disc space-y-1 text-sm text-red-600">
                {formatValidationErrors(validationErrors).map((err, idx) => (
                  <li key={idx}>
                    <span className="font-medium">{err.field}:</span>{" "}
                    {err.messages.join(", ")}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="space-y-6">
          {/* Basic Info */}
          <Card className="p-4 sm:p-6 space-y-5">
            <h3 className="font-semibold text-[#102040]">Lesson Details</h3>

            <div>
              <label className="text-[14px] font-normal text-[#102040] tracking-[0.5px] mb-2 block">
                Lesson Title <span className="text-[#FF0000]">*</span>
              </label>
              <Input
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter lesson title"
                required
                className={inputClassName}
              />
            </div>

            <div>
              <label className="text-[14px] font-normal text-[#102040] tracking-[0.5px] mb-2 block">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className={inputClassName + " resize-none"}
                placeholder="Brief description of this lesson..."
              />
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="text-[14px] font-normal text-[#102040] tracking-[0.5px] mb-2 block">
                  Order <span className="text-[#FF0000]">*</span>
                </label>
                <Input
                  type="number"
                  name="order"
                  value={formData.order}
                  onChange={handleChange}
                  placeholder="1"
                  required
                  min={1}
                  className={inputClassName}
                />
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isFree"
                  name="isFree"
                  checked={formData.isFree}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-gray-300 text-[#e07828] focus:ring-[#e07828]"
                />
                <label htmlFor="isFree" className="text-[14px] text-[#102040]">
                  Free Preview
                </label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isPublished"
                  name="isPublished"
                  checked={formData.isPublished}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-gray-300 text-[#e07828] focus:ring-[#e07828]"
                />
                <label
                  htmlFor="isPublished"
                  className="text-[14px] text-[#102040]"
                >
                  Published
                </label>
              </div>
            </div>
          </Card>

          {/* Content Type Selector */}
          <Card className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#102040]">Lesson Content</h3>
              <div className="flex items-center gap-2">
                {isCalculating ? (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-full border border-amber-100">
                    <Calculator
                      size={14}
                      className="text-amber-600 animate-spin"
                    />
                    <span className="text-[12px] font-medium text-amber-700">
                      Estimating...
                    </span>
                  </div>
                ) : formData.isManualDuration ? (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-lg border border-blue-200">
                      <Clock size={14} className="text-blue-600" />
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          className="w-10 bg-transparent border-none text-[12px] font-bold text-blue-700 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          value={Math.floor(formData.duration / 60)}
                          onChange={(e) =>
                            handleManualDurationChange(
                              Math.max(0, parseInt(e.target.value) || 0),
                              formData.duration % 60,
                            )
                          }
                          placeholder="M"
                        />
                        <span className="text-[10px] text-blue-400 font-medium">
                          m
                        </span>
                        <input
                          type="number"
                          className="w-10 bg-transparent border-none text-[12px] font-bold text-blue-700 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          value={formData.duration % 60}
                          onChange={(e) =>
                            handleManualDurationChange(
                              Math.floor(formData.duration / 60),
                              Math.max(0, parseInt(e.target.value) || 0),
                            )
                          }
                          placeholder="S"
                        />
                        <span className="text-[10px] text-blue-400 font-medium">
                          s
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          isManualDuration: false,
                        }))
                      }
                      className="text-[10px] font-medium text-gray-500 hover:text-amber-600 transition-colors flex items-center gap-1 px-2 py-1 rounded hover:bg-amber-50"
                    >
                      <Calculator size={10} /> Reset to Auto
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    {formData.duration > 0 && (
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-full border border-blue-100 transition-all opacity-100 scale-100">
                        <Clock size={14} className="text-blue-600" />
                        <span className="text-[12px] font-bold text-blue-700">
                          {formatDuration(formData.duration)}
                        </span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          isManualDuration: true,
                        }))
                      }
                      className="text-[10px] font-medium text-gray-400 hover:text-blue-600 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                    >
                      {formData.duration > 0
                        ? "Override"
                        : "Set Custom Duration"}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <button
                type="button"
                onClick={() =>
                  setFormData((prev) => ({ ...prev, contentType: "video" }))
                }
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  formData.contentType === "video"
                    ? "border-[#e07828] bg-[#e07828]/5"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <Video
                  size={24}
                  className={
                    formData.contentType === "video"
                      ? "text-[#e07828] mx-auto mb-2"
                      : "text-gray-400 mx-auto mb-2"
                  }
                />
                <p
                  className={`text-sm font-medium ${
                    formData.contentType === "video"
                      ? "text-[#e07828]"
                      : "text-gray-600"
                  }`}
                >
                  Video Lesson
                </p>
              </button>

              <button
                type="button"
                onClick={() =>
                  setFormData((prev) => ({ ...prev, contentType: "article" }))
                }
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  formData.contentType === "article"
                    ? "border-[#e07828] bg-[#e07828]/5"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <FileText
                  size={24}
                  className={
                    formData.contentType === "article"
                      ? "text-[#e07828] mx-auto mb-2"
                      : "text-gray-400 mx-auto mb-2"
                  }
                />
                <p
                  className={`text-sm font-medium ${
                    formData.contentType === "article"
                      ? "text-[#e07828]"
                      : "text-gray-600"
                  }`}
                >
                  Article / Text
                </p>
              </button>
            </div>

            {formData.contentType === "video" ? (
              <div className="space-y-4">
                <div>
                  <label className="text-[14px] font-normal text-[#102040] tracking-[0.5px] mb-2 block">
                    Video URL
                  </label>
                  <Input
                    type="url"
                    name="videoUrl"
                    value={formData.videoUrl}
                    onChange={handleChange}
                    placeholder="https://youtube.com/watch?v=..."
                    className={inputClassName}
                  />
                  <p className="text-[12px] text-[#706F6F] mt-1">
                    YouTube, Vimeo, or direct video URL
                  </p>
                </div>

                <div>
                  <label className="text-[14px] font-normal text-[#102040] tracking-[0.5px] mb-2 block">
                    VdoCipher Video ID (Optional)
                  </label>
                  <div className="flex gap-2">
                    <Input
                      name="vdocipherVideoId"
                      value={formData.vdocipherVideoId}
                      onChange={handleChange}
                      placeholder="Enter VdoCipher video ID"
                      className={inputClassName}
                    />
                    <div className="relative">
                      <input
                        type="file"
                        id="vdocipher-upload"
                        accept="video/*"
                        className="hidden"
                        onChange={handleVideoUpload}
                        disabled={isUploadingVideo}
                      />
                      <label
                        htmlFor="vdocipher-upload"
                        className={`flex items-center justify-center h-[47px] px-4 bg-[#102040] text-white rounded-[8px] cursor-pointer hover:bg-[#102040]/90 transition-all ${
                          isUploadingVideo
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        {isUploadingVideo ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <Upload size={18} />
                        )}
                        <span className="ml-2 whitespace-nowrap text-sm font-medium">
                          {isUploadingVideo ? `${uploadProgress}%` : "Upload"}
                        </span>
                      </label>
                    </div>
                  </div>
                  <p className="text-[12px] text-[#706F6F] mt-1">
                    Enter ID manually or upload a new video directly to
                    VdoCipher
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <label className="text-[14px] font-normal text-[#102040] tracking-[0.5px] mb-2 block">
                  Article Content
                </label>
                <textarea
                  name="articleText"
                  value={formData.articleText}
                  onChange={handleChange}
                  rows={10}
                  className={inputClassName + " resize-none font-mono text-sm"}
                  placeholder="Write your lesson content here... (Markdown supported)"
                />
              </div>
            )}
          </Card>

          {/* Attachments */}
          <Card className="p-4 sm:p-6">
            <h3 className="font-semibold text-[#102040] mb-4">Attachments</h3>

            {formData.attachments.length > 0 && (
              <div className="space-y-2 mb-4">
                {formData.attachments.map((attachment, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-gray-500" />
                      <span className="text-sm text-gray-700">
                        {attachment.name}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <label
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`flex flex-col items-center justify-center gap-2 p-8 border-2 border-dashed rounded-lg cursor-pointer transition-all ${
                isDragging
                  ? "border-[#e07828] bg-[#e07828]/5 scale-[1.01]"
                  : "border-gray-200 hover:border-[#e07828] hover:bg-gray-50"
              }`}
            >
              <input
                type="file"
                onChange={handleAttachmentUpload}
                className="hidden"
                disabled={isUploading}
                multiple
              />
              {isUploading ? (
                <div className="flex flex-col items-center gap-4 w-full max-w-xs">
                  <div className="relative w-full h-12 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-[#e07828] absolute" />
                  </div>

                  <div className="w-full space-y-2">
                    <div className="flex justify-between text-xs font-medium text-[#e07828]">
                      {(() => {
                        const progressValues = Object.values(
                          attachmentUploadProgress,
                        );
                        const avgProgress = Math.round(
                          progressValues.reduce((a, b) => a + b, 0) /
                            (progressValues.length || 1),
                        );

                        return (
                          <>
                            <span>
                              {avgProgress >= 100
                                ? "Finalizing..."
                                : Object.keys(attachmentUploadProgress).length >
                                    1
                                  ? "Total Progress"
                                  : "Uploading..."}
                            </span>
                            <span>{avgProgress}%</span>
                          </>
                        );
                      })()}
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ease-out ${
                          Math.round(
                            Object.values(attachmentUploadProgress).reduce(
                              (a, b) => a + b,
                              0,
                            ) /
                              (Object.keys(attachmentUploadProgress).length ||
                                1),
                          ) >= 100
                            ? "bg-green-500 animate-pulse"
                            : "bg-[#e07828]"
                        }`}
                        style={{
                          width: `${Math.round(
                            Object.values(attachmentUploadProgress).reduce(
                              (a, b) => a + b,
                              0,
                            ) /
                              (Object.keys(attachmentUploadProgress).length ||
                                1),
                          )}%`,
                        }}
                      />
                    </div>
                    {Object.keys(attachmentUploadProgress).length > 0 && (
                      <div className="text-[10px] text-gray-500 text-center truncate">
                        {Object.keys(attachmentUploadProgress).length === 1
                          ? Object.keys(attachmentUploadProgress)[0]
                          : `${Object.keys(attachmentUploadProgress).length} files uploading...`}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div
                    className={`p-3 rounded-full transition-colors ${
                      isDragging ? "bg-[#e07828]/10" : "bg-gray-100"
                    }`}
                  >
                    <Upload
                      size={24}
                      className={
                        isDragging ? "text-[#e07828]" : "text-gray-400"
                      }
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-[#102040]">
                      Click to upload or drag and drop
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      PDF, DOCX, ZIP, etc. (Unlimited size)
                    </p>
                  </div>
                </>
              )}
            </label>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 mt-6 pb-8">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/courses/${courseId}/structure`)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isEditMode ? "Update Lesson" : "Create Lesson"}
          </Button>
        </div>
      </form>
    </DashboardLayout>
  );
}
